import { supabase } from "@/lib/supabase";
import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import {
  type TimelineSummaryApprovalResult,
  type TimelineSummaryPreview,
} from "@/features/timeline";
import type { SubscriptionTier } from "@/types/projects";

type TimelineSummaryAction = "preview" | "approve";

export type TimelineSummaryErrorCode =
  | "tier-locked"
  | "insufficient-credits"
  | "access-denied"
  | "not-found"
  | "stale"
  | "token-invalid"
  | "failed";

type TimelineSummaryErrorBody = {
  code?: string;
  currentBalance?: number | null;
  message?: string;
  recommendedTier?: SubscriptionTier;
};

export class TimelineSummaryError extends Error {
  constructor(
    public readonly code: TimelineSummaryErrorCode,
    message: string,
    public readonly currentBalance: number | null = null,
    public readonly recommendedTier: SubscriptionTier | null = null,
  ) {
    super(message);
    this.name = "TimelineSummaryError";
  }
}

async function readTimelineSummaryErrorBody(error: unknown) {
  if (typeof error === "object" && error) {
    const response =
      ("context" in error ? (error as { context?: Response }).context : undefined)
      ?? ("response" in error ? (error as { response?: Response }).response : undefined);

    if (response) {
      try {
        return await response.json() as TimelineSummaryErrorBody;
      } catch {
        return null;
      }
    }
  }

  return null;
}

async function normalizeTimelineSummaryError(error: unknown) {
  if (error instanceof TimelineSummaryError) {
    return error;
  }

  const body = await readTimelineSummaryErrorBody(error);
  const code = body?.code ?? (error instanceof Error ? error.message : "TIMELINE_SUMMARY_FAILED");

  switch (code) {
    case "TIER_LOCKED":
      return new TimelineSummaryError(
        "tier-locked",
        body?.message ?? code,
        body?.currentBalance ?? null,
        body?.recommendedTier ?? "crafter",
      );
    case "INSUFFICIENT_CREDITS":
      return new TimelineSummaryError(
        "insufficient-credits",
        body?.message ?? code,
        body?.currentBalance ?? 0,
        body?.recommendedTier ?? null,
      );
    case "PROJECT_ACCESS_DENIED":
      return new TimelineSummaryError("access-denied", body?.message ?? code);
    case "TIMELINE_EVENT_NOT_FOUND":
      return new TimelineSummaryError("not-found", body?.message ?? code);
    case "TIMELINE_SUMMARY_STALE":
      return new TimelineSummaryError("stale", body?.message ?? code);
    case "TIMELINE_SUMMARY_TOKEN_INVALID":
      return new TimelineSummaryError("token-invalid", body?.message ?? code);
    default:
      return new TimelineSummaryError("failed", body?.message ?? code);
  }
}

export function formatTimelineSummaryErrorMessage(error: TimelineSummaryError) {
  switch (error.code) {
    case "tier-locked":
      return "timeline.summary.errors.tierLocked";
    case "insufficient-credits":
      return "timeline.summary.errors.insufficientCredits";
    case "access-denied":
      return "timeline.summary.errors.accessDenied";
    case "not-found":
      return "timeline.summary.errors.notFound";
    case "stale":
      return "timeline.summary.errors.stale";
    case "token-invalid":
      return "timeline.summary.errors.tokenInvalid";
    default:
      return "timeline.summary.errors.failed";
  }
}

async function invokeTimelineSummary<T>(action: TimelineSummaryAction, body: Record<string, unknown>) {
  if (!isSupabaseConfigured()) {
    throw new TimelineSummaryError("failed", "TIMELINE_SUMMARY_FAILED");
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new TimelineSummaryError("access-denied", "PROJECT_ACCESS_DENIED");
  }

  const response = await fetch(`${getClientEnv().supabaseUrl}/functions/v1/timeline-summary`, {
    body: JSON.stringify({
      action,
      ...body,
    }),
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    throw await normalizeTimelineSummaryError({
      context: {
        json: async () => responseBody,
      },
      message:
        typeof responseBody === "object" && responseBody && "code" in responseBody
          ? String(responseBody.code)
          : `TIMELINE_SUMMARY_FAILED_${response.status}`,
    });
  }

  return responseBody as T;
}

export async function previewTimelineSummary(
  projectId: string,
  eventId: string,
): Promise<TimelineSummaryPreview> {
  const data = await invokeTimelineSummary<TimelineSummaryPreview>("preview", {
    eventId,
    projectId,
  });

  if (!data) {
    throw new TimelineSummaryError("failed", "TIMELINE_SUMMARY_FAILED");
  }

  return data;
}

export async function approveTimelineSummary(
  projectId: string,
  eventId: string,
  approvalToken: string,
): Promise<TimelineSummaryApprovalResult> {
  const data = await invokeTimelineSummary<TimelineSummaryApprovalResult>("approve", {
    approvalToken,
    eventId,
    projectId,
  });

  if (!data) {
    throw new TimelineSummaryError("failed", "TIMELINE_SUMMARY_FAILED");
  }

  return data;
}
